"use client"
import type React from "react"
import { useState, useEffect } from "react"
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tooltip,
  Badge,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
} from "@mui/material"
import {
  Assignment as AssignmentIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material"

import { IonContent, IonPage } from "@ionic/react"
import { getAllServices, assignStaffToService, assignStaffToProject, projectDelete } from "../../../api/services/services"
import { getStaffList } from "../../../api/stock-transfer/stock-transfer"
import { getAllEmployees, getAllEmployees_active } from "../../../api/BusinessOwner"

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

interface AssignedService {
  assigned_services_id: string
  service_id: string
  priority: string
  note: string
  staff: StaffMember[]
}

interface StaffMember {
  user_id: string
  fullName: string
  email: string
  phone: string
}

interface APIProject {
  project_id: string
  project_name: string
  tags: string
  budget_cost: string
  status: string
  created_at: string
}

interface APIServiceRequest {
  id: number
  intake_id: string
  user_id: string
  service_type: string
  selected_services: string[]
  status: string
  created_at: string
  assigned_services?: AssignedService[]
}

interface StaffType {
  user_id: string
  fullName: string
  email: string
  phone: string
  roleName: string
  department: string
  status: boolean
  workload: number
  avatar?: string
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

export const AdminServiceRequests: React.FC = () => {
  const [projects, setProjects] = useState<APIProject[]>([])
  const [intakes, setIntakes] = useState<APIServiceRequest[]>([])
  const [staff, setStaff] = useState<StaffType[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedProject, setSelectedProject] = useState<APIProject | null>(null)
  const [selectedIntake, setSelectedIntake] = useState<APIServiceRequest | null>(null)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [assignProjectDialogOpen, setAssignProjectDialogOpen] = useState(false)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<APIProject | null>(null)
  const [selectedStaff, setSelectedStaff] = useState("")
  const [assignmentNotes, setAssignmentNotes] = useState("")
  const [assignmentPriority, setAssignmentPriority] = useState<"low" | "medium" | "high">("medium")
  const [tabValue, setTabValue] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>("all")

  const fetchStaffList = async () => {
    try {
      setLoading(true)
      const response = await getAllEmployees_active()
      console.log("response",response)
      if (response) {
        setStaff(response || [])
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await getAllServices()

      if (response.status === true) {
        setProjects(response.data.projects || [])
        setIntakes(response.data.intakes || [])
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    fetchStaffList()
  }, [])

  const handleAssignIntake = (intake: APIServiceRequest) => {
    setSelectedIntake(intake)
    setAssignDialogOpen(true)
    setSelectedStaff("")
    setAssignmentNotes("")
    setAssignmentPriority("medium")
  }

  const handleAssignProject = (project: APIProject) => {
    setSelectedProject(project)
    setAssignProjectDialogOpen(true)
    setSelectedStaff("")
    setAssignmentNotes("")
    setAssignmentPriority("medium")
  }

  const handleViewProject = (project: APIProject) => {
    setSelectedProject(project)
    setViewDialogOpen(true)
  }

  const handleViewIntake = (intake: APIServiceRequest) => {
    setSelectedIntake(intake)
    setViewDialogOpen(true)
  }

  const handleDeleteProject = (project: APIProject) => {
    setProjectToDelete(project)
    setDeleteDialogOpen(true)
  }

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return

    try {
      // Replace this with your actual API endpoint
      // const response = await fetch(`/api/projects/${projectToDelete.project_id}`, {
      //   method: 'DELETE',
      // })

      const response = await projectDelete(projectToDelete.project_id); // Dummy API call for illustration
      
      if (response.status === true) {
        // Remove the project from state
        setProjects(prev => prev.filter(project => project.project_id !== projectToDelete.project_id))
        setDeleteDialogOpen(false)
        setProjectToDelete(null)
        
        // Show success message or refresh data
        fetchData() // Refresh the data
      } else {
        console.error('Failed to delete project')
      }
    } catch (error) {
      console.error('Error deleting project:', error)
    }
  }

  const handleIntakeStatusChange = (intakeId: number, newStatus: string) => {
    setIntakes((prev) =>
      prev.map((intake) => (intake.id === intakeId ? { ...intake, status: newStatus as any } : intake)),
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning"
      case "assigned":
        return "info"
      case "in-progress":
        return "primary"
      case "completed":
        return "success"
      case "rejected":
        return "error"
      default:
        return "default"
    }
  }

  const getPriorityColor = (priority: "low" | "medium" | "high") => {
    switch (priority) {
      case "high":
        return "error"
      case "medium":
        return "warning"
      case "low":
        return "success"
      default:
        return "default"
    }
  }

  const getAssignedStaff = (staffId: string) => {
    return staff.find((s) => s.user_id === staffId)
  }

  // Helper function to format numbers with thousand separators
  const formatNumber = (number) => {
    if (number === null || number === undefined || number === '') return '0';
    return Number(number).toLocaleString('en-US');
  };

  // Helper function to format amount in Naira
  const formatNairaDetailed = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '₦0';
    
    const numberAmount = Number(amount);
    if (isNaN(numberAmount)) return '₦0.00';
    
    return `₦${numberAmount.toLocaleString('en-US')}`;
  };

  // Alternative more detailed formatting function if you need decimal places
  const formatNaira = (amount, decimalPlaces = 2) => {
    if (amount === null || amount === undefined || amount === '') return '₦0.00';
    
    const numberAmount = Number(amount);
    if (isNaN(numberAmount)) return '₦0.00';
    
    return `₦${numberAmount.toLocaleString('en-US', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    })}`;
  };

  const parseTags = (tagsString: string): string[] => {
    try {
      if (!tagsString) return []
      const cleanedString = tagsString.replace(/^"|"$/g, "").replace(/\\"/g, "")
      return cleanedString
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
    } catch (error) {
      return []
    }
  }

  const getProjectsByStatus = (status: string) => {
    return projects.filter((project) => project.status.toLowerCase() === status.toLowerCase()).length
  }

  const getIntakesByStatus = (status: string) => {
    return intakes.filter((intake) => intake.status.toLowerCase() === status.toLowerCase()).length
  }

  const getTotalByStatus = (status: string) => {
    return getProjectsByStatus(status) + getIntakesByStatus(status)
  }

  const onhandleAssignIntake = async () => {
    const data = {
      services_id: selectedIntake?.intake_id,
      selectedStaff,
      assignmentNotes,
      assignmentPriority,
    }

    try {
      const response = await assignStaffToService(data)

      const refreshResponse = await getAllServices()
      if (refreshResponse.status === true) {
        setIntakes(refreshResponse.data.intakes || [])

        fetchData()
        fetchStaffList()
      }

      setAssignDialogOpen(false)
      setSelectedIntake(null)
    } catch (error) {
      console.log(error)
    }
  }

  const onhandleAssignProject = async () => {
    const data = {
      project_id: selectedProject?.project_id,
      selectedStaff,
      assignmentNotes,
      assignmentPriority,
    }

    try {
      const response = await assignStaffToProject(data)
      console.log("refreshResponse",response)

      // Refresh data after assignment
      const refreshResponse = await getAllServices()

      fetchData()
      fetchStaffList()

      setAssignProjectDialogOpen(false)
      setSelectedProject(null)
    } catch (error) {
      console.log(error)
    }
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ textAlign: "center", py: 8 }}>
          <LinearProgress sx={{ mb: 2 }} />
          <Typography>Loading service requests...</Typography>
        </Box>
      </Container>
    )
  }

  console.log({ intakes, projects })

  function FormatCurency(amount: number, decimalPlaces = 2): React.ReactNode {
    if (amount === null || amount === undefined || amount === '' || isNaN(Number(amount))) {
      return `₦${(0).toFixed(decimalPlaces)}`;
    }
    const num = Number(amount);
    return `₦${num.toLocaleString("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    })}`;
  } 

  return (
    <IonPage className="pushDown">
      <IonContent>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#000", mb: 1 }}>
                Service Requests Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage and assign service requests to your team
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              sx={{ borderColor: "#000", color: "#000" }}
            >
              Refresh
            </Button>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ border: "2px solid #f0f0f0" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Pending
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {getTotalByStatus("pending")}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: "#ff9800" }}>
                      <ScheduleIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ border: "2px solid #f0f0f0" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        In Progress
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {getTotalByStatus("in-progress")}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: "#9c27b0" }}>
                      <EditIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ border: "2px solid #f0f0f0" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Completed
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {getTotalByStatus("completed")}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: "#4caf50" }}>
                      <CheckCircleIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ border: "2px solid #f0f0f0" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Cancelled
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {getTotalByStatus("cancelled")}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: "#f44336" }}>
                      <RefreshIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters and Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                <Tab label={`Recruitment Projects (${projects.length})`} />
                <Tab label={`HR Service Intakes (${intakes.length})`} />
                <Tab label="Team Overview" />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {/* Projects Table */}
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      {/* <TableCell sx={{ fontWeight: 700 }}>Project ID</TableCell> */}
                      <TableCell sx={{ fontWeight: 700 }}>Project Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Tags</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Budget</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((project, index) => (
                      <TableRow key={index} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
                        {/* <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {project.project_id || `#${index + 1}`}
                          </Typography>
                        </TableCell> */}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {project.project_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {parseTags(project.tags)
                              .slice(0, 3)
                              .map((tag, tagIndex) => (
                                <Chip
                                  key={tagIndex}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: "0.7rem", height: "20px" }}
                                />
                              ))}
                            {parseTags(project.tags).length > 3 && (
                              <Chip
                                label={`+${parseTags(project.tags).length - 3}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: "20px" }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{FormatCurency(Number(project.budget_cost))}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={project.status.replace("_", " ").toUpperCase()}
                            color={getStatusColor(project.status.toLowerCase()) as any}
                            variant="filled"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{new Date(project.created_at).toLocaleDateString()}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => handleViewProject(project)}>
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Assign to Staff">
                              <IconButton size="small" onClick={() => handleAssignProject(project)}>
                                <AssignmentIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Project">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteProject(project)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {/* Intakes Table */}
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Intake ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Service Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Selected Services</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Assigned Staff</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {intakes.map((intake, index) => (
                      <TableRow key={index} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            #{intake.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={intake.service_type.replace("-", " ").toUpperCase()}
                            variant="outlined"
                            size="small"
                            icon={<PersonIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {intake.selected_services.map((service, serviceIndex) => (
                              <Chip
                                key={serviceIndex}
                                label={service.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem" }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={intake?.status}
                            color={getStatusColor(intake?.status) as any}
                            variant="filled"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {intake.assigned_services && intake.assigned_services.length > 0 ? (
                            <Box>
                              {intake.assigned_services.map((assignment, assignIndex) => (
                                <Box key={assignIndex} sx={{ mb: 1 }}>
                                  {assignment.staff.map((staffMember, staffIndex) => (
                                    <Box
                                      key={staffIndex}
                                      sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                                    >
                                      <Avatar sx={{ width: 24, height: 24 }}>{staffMember.fullName.charAt(0)}</Avatar>
                                      <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                          {staffMember.fullName}
                                        </Typography>
                                        <Chip
                                          label={assignment.priority.toUpperCase()}
                                          color={getPriorityColor(assignment.priority as any) as any}
                                          size="small"
                                          sx={{ ml: 1, height: "16px", fontSize: "0.6rem" }}
                                        />
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Unassigned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{new Date(intake.created_at).toLocaleDateString()}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => handleViewIntake(intake)}>
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            {(!intake.assigned_services || intake.assigned_services.length === 0) && (
                              <Tooltip title="Assign to Staff">
                                <IconButton size="small" onClick={() => handleAssignIntake(intake)}>
                                  <AssignmentIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {/* Team Overview */}
              <Box sx={{ px: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Team Workload Overview
                </Typography>
                <Grid container spacing={3}>
                  {staff?.map((member, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Card sx={{ border: member?.status ? "2px solid #e0e0e0" : "2px solid #f0f0f0" }}>
                        <CardContent>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Badge
                              color={member?.status ? "success" : "default"}
                              variant="dot"
                              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            >
                              <Avatar src={member?.avatar} sx={{ width: 48, height: 48, mr: 2 }}>
                                {member?.fullName}
                              </Avatar>
                            </Badge>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {member?.fullName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {member?.roleName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {member?.department}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="body2">
                              Active Assignments: <strong>{member?.workload}</strong>
                            </Typography>
                            <Chip
                              label={member?.status ? "Active" : "Inactive"}
                              color={member?.status ? "success" : "default"}
                              size="small"
                            />
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min((member?.workload / 10) * 100, 100)}
                            sx={{ mt: 1 }}
                            color={member?.workload > 7 ? "error" : member?.workload > 4 ? "warning" : "success"}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </TabPanel>
          </Paper>

          {/* Assignment Dialog for Intakes */}
          <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ bgcolor: "#000", color: "white" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Assign Request to Staff Member
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {selectedIntake && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Request:</strong> {selectedIntake.intake_id}
                      <br />
                      <strong>Client:</strong> {selectedIntake.user_id}
                      <br />
                      <strong>Service Type:</strong> {selectedIntake.service_type.replace("-", " ").toUpperCase()}
                    </Typography>
                  </Alert>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Select Staff Member</InputLabel>
                        <Select
                          value={selectedStaff}
                          label="Select Staff Member"
                          onChange={(e) => setSelectedStaff(e.target.value)}
                        >
                          {staff
                            .filter((s) => s.status)
                            .map((member) => (
                              <MenuItem key={member.user_id} value={member.user_id}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                                  <Avatar src={member.avatar} sx={{ width: 32, height: 32 }}>
                                    {member.fullName.charAt(0)}
                                  </Avatar>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {member.fullName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {member.roleName} • {member.workload} active assignments
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={`Load: ${member.workload}`}
                                    size="small"
                                    color={member.workload > 7 ? "error" : member.workload > 4 ? "warning" : "success"}
                                  />
                                </Box>
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Priority Level</InputLabel>
                        <Select
                          value={assignmentPriority}
                          label="Priority Level"
                          onChange={(e) => setAssignmentPriority(e.target.value as "low" | "medium" | "high")}
                        >
                          <MenuItem value="low">Low Priority</MenuItem>
                          <MenuItem value="medium">Medium Priority</MenuItem>
                          <MenuItem value="high">High Priority</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Assignment Notes"
                        placeholder="Add any special instructions or notes for the assigned staff member..."
                        value={assignmentNotes}
                        onChange={(e) => setAssignmentNotes(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setAssignDialogOpen(false)} variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={onhandleAssignIntake}
                variant="contained"
                disabled={!selectedStaff}
                sx={{ bgcolor: "#000", "&:hover": { bgcolor: "#333" } }}
              >
                Assign Request
              </Button>
            </DialogActions>
          </Dialog>

          {/* Assignment Dialog for Projects */}
          <Dialog
            open={assignProjectDialogOpen}
            onClose={() => setAssignProjectDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ bgcolor: "#000", color: "white" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Assign Project to Staff Member
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {selectedProject && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Project:</strong> {selectedProject.project_name}
                      <br />
                      {/* <strong>Project ID:</strong> {selectedProject.project_id}
                      <br /> */}
                      <strong>Budget:</strong> {formatNaira(selectedProject.budget_cost)}
                    </Typography>
                  </Alert>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Select Staff Member</InputLabel>
                        <Select
                          value={selectedStaff}
                          label="Select Staff Member"
                          onChange={(e) => setSelectedStaff(e.target.value)}
                        >
                          {staff
                            .filter((s) => s.status)
                            .map((member) => (
                              <MenuItem key={member.user_id} value={member.user_id}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                                  <Avatar src={member.avatar} sx={{ width: 32, height: 32 }}>
                                    {member.fullName.charAt(0)}
                                  </Avatar>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {member.fullName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {member.roleName} • {formatNumber(member.workload)} active assignment{member.workload !== 1 ? 's' : ''}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={`Load: ${formatNumber(member.workload)}`}
                                    size="small"
                                    color={member.workload > 7 ? "error" : member.workload > 4 ? "warning" : "success"}
                                  />
                                </Box>
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Priority Level</InputLabel>
                        <Select
                          value={assignmentPriority}
                          label="Priority Level"
                          onChange={(e) => setAssignmentPriority(e.target.value as "low" | "medium" | "high")}
                        >
                          <MenuItem value="low">Low Priority</MenuItem>
                          <MenuItem value="medium">Medium Priority</MenuItem>
                          <MenuItem value="high">High Priority</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Assignment Notes"
                        placeholder="Add any special instructions or notes for the assigned staff member..."
                        value={assignmentNotes}
                        onChange={(e) => setAssignmentNotes(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setAssignProjectDialogOpen(false)} variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={onhandleAssignProject}
                variant="contained"
                disabled={!selectedStaff}
                sx={{ bgcolor: "#000", "&:hover": { bgcolor: "#333" } }}
              >
                Assign Project
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: "#000", color: "white" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Delete Project
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Are you sure you want to delete this project?
                </Typography>
              </Alert>
              {projectToDelete && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Project Name: <strong>{projectToDelete.project_name}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Project ID: <strong>{projectToDelete.project_id}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This action cannot be undone and will permanently remove the project from the system.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button 
                onClick={() => setDeleteDialogOpen(false)} 
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteProject}
                variant="contained"
                color="error"
                sx={{ 
                  bgcolor: "#d32f2f", 
                  "&:hover": { bgcolor: "#b71c1c" } 
                }}
              >
                Yes, Delete Project
              </Button>
            </DialogActions>
          </Dialog>

          {/* View Request Dialog */}
          <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ bgcolor: "#000", color: "white" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Request Details
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {selectedProject && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Project Information
                      </Typography>
                      {/* <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Project ID
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                          {selectedProject.project_id || `#${selectedProject.project_name}`}
                        </Typography>
                      </Box> */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Project Name
                        </Typography>
                        <Typography variant="body1">{selectedProject.project_name}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={selectedProject.status.replace("_", " ").toUpperCase()}
                          color={getStatusColor(selectedProject.status.toLowerCase()) as any}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Created Date
                        </Typography>
                        <Typography variant="body1">
                          {new Date(selectedProject.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Project Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Budget Range
                            </Typography>
                            <Typography variant="body1">{Number(selectedProject.budget_cost).toLocaleString("en-US", { style: "currency", currency: "NGN" })}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Tags
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                              {parseTags(selectedProject.tags).map((tag, index) => (
                                <Chip key={index} label={tag} variant="outlined" size="small" />
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {selectedIntake && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Intake Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Intake ID
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                          #{selectedIntake.id}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Service Type
                        </Typography>
                        <Chip
                          label={selectedIntake.service_type.replace("-", " ").toUpperCase()}
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={selectedIntake?.status.toUpperCase()}
                          color={getStatusColor(selectedIntake.status) as any}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Created Date
                        </Typography>
                        <Typography variant="body1">
                          {new Date(selectedIntake.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Intake Details
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Selected Services
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                              {selectedIntake.selected_services.map((service, index) => (
                                <Chip
                                  key={index}
                                  label={service.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                  variant="outlined"
                                  size="small"
                                />
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Created Date
                            </Typography>
                            <Typography variant="body1">
                              {new Date(selectedIntake.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {selectedIntake.assigned_services && selectedIntake.assigned_services.length > 0 && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, border: "1px solid #e0e0e0" }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Assignment Details
                        </Typography>
                        {selectedIntake.assigned_services.map((assignment, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, border: "1px solid #f0f0f0", borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              Assignment #{index + 1}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Priority
                                </Typography>
                                <Chip
                                  label={assignment.priority.toUpperCase()}
                                  color={getPriorityColor(assignment.priority as any) as any}
                                  size="small"
                                  sx={{ mt: 0.5 }}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Note
                                </Typography>
                                <Typography variant="body2">{assignment.note}</Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Assigned Staff
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                  {assignment.staff.map((staffMember, staffIndex) => (
                                    <Box
                                      key={staffIndex}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        p: 1,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Avatar sx={{ width: 32, height: 32 }}>{staffMember.fullName.charAt(0)}</Avatar>
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {staffMember.fullName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {staffMember.email}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </IonContent>
    </IonPage>
  )
}